import React from "react";
import { useNavigate } from "react-router-dom";

/* Shared Equal brand primitives — tokens live in index.css / tailwind.config.js */

export const EqualMark = ({ size = 24, color = "#17a35f" }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <path d="M16 2L29 9.5V22.5L16 30L3 22.5V9.5L16 2Z" stroke={color} strokeWidth="1.5" strokeLinejoin="round" fill="none" />
    <path d="M16 8L24 12.5V21.5L16 26L8 21.5V12.5L16 8Z" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1" strokeLinejoin="round" />
    <circle cx="16" cy="16" r="2.5" fill={color} />
  </svg>
);

export const Eyebrow = ({ children, className = "", ...rest }) => (
  <p className={`eyebrow ${className}`} {...rest}>{children}</p>
);

export const Brand = ({ section, hint, onClick }) => (
  <div className={`flex items-center gap-2.5 min-w-0 ${onClick ? "cursor-pointer" : ""}`} onClick={onClick}>
    <EqualMark size={22} />
    <span className="text-ink font-bold text-[17px] tracking-tight">Equal</span>
    {section && (
      <>
        <span className="text-[#c9d1d9] text-sm">/</span>
        <span className="text-mute text-sm truncate">{section}</span>
      </>
    )}
    {hint && <span className="hidden md:inline eyebrow ml-2">{hint}</span>}
  </div>
);

export const AppHeader = ({ section, hint, children, onBrandClick, className = "" }) => (
  <header className={`sticky top-0 z-20 h-14 bg-white border-b border-line px-5 md:px-7 flex items-center justify-between gap-4 ${className}`}>
    <Brand section={section} hint={hint} onClick={onBrandClick} />
    <div className="flex items-center gap-2 flex-wrap justify-end">{children}</div>
  </header>
);

export const UnlockOverlay = ({ label }) => (
  <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white">
    <div className="relative w-14 h-14 flex items-center justify-center">
      <div className="absolute inset-0 rounded-full border border-equal/40 animate-ping" />
      <EqualMark size={40} />
    </div>
    <p className="mt-5 eyebrow text-equal-dark animate-pulse">{label}</p>
  </div>
);

export const EyeToggle = ({ shown, onClick }) => (
  <button type="button" onClick={onClick} tabIndex={-1}
    className="absolute right-3 top-1/2 -translate-y-1/2 text-mute hover:text-ink transition-colors">
    {shown
      ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
      : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
  </button>
);

/* Numbered step marker used by rails, modals and checklists */
export const StepNumber = ({ n, active = false, size = 22 }) => (
  <span
    className={`font-mono text-[11px] rounded-full border-[1.5px] grid place-items-center flex-none ${active ? "bg-equal border-equal text-white" : "border-line text-ink-2 bg-white"}`}
    style={{ width: size, height: size }}>
    {n}
  </span>
);

/* Light auth layout: white header, mint hero band, white card overlapping the band */
export const AuthShell = ({ eyebrow, title, subtitle, headerRight, children, after, footer, brandTo = "/" }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col bg-page">
      <header className="h-14 bg-white border-b border-line px-5 md:px-8 flex items-center justify-between">
        <Brand onClick={() => navigate(brandTo)} />
        {headerRight}
      </header>
      <main className="flex-1">
        <div className="bg-mint border-b border-[#cfe9db]">
          <div className="max-w-5xl mx-auto px-5 md:px-8 pt-14 pb-14 lg:pb-32">
            <div className="lg:max-w-[calc(100%-440px)] animate-rise-in">
              {eyebrow && <Eyebrow className="text-equal-dark">{eyebrow}</Eyebrow>}
              <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight text-ink leading-[1.05]">{title}</h1>
              {subtitle && <p className="mt-4 text-mute text-base max-w-md leading-relaxed">{subtitle}</p>}
            </div>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-5 md:px-8 pb-16 lg:grid lg:grid-cols-[1fr_400px] lg:gap-12">
          <div className="hidden lg:block" />
          <div className="-mt-8 lg:-mt-28 animate-rise-in" style={{ animationDelay: "80ms" }}>
            <div className="card-flat p-7 space-y-4">{children}</div>
            {after}
          </div>
        </div>
      </main>
      {footer}
    </div>
  );
};

export const AuthFooter = ({ children }) => (
  <div className="py-5 text-center border-t border-line bg-white">
    <p className="eyebrow">{children}</p>
  </div>
);
